import { Component, Inject, Input, OnInit, OnDestroy, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GadgetComplete } from 'node_js/models/business/lab/gadget-complete';
import { GadgetUserComplete } from 'node_js/models/business/lab/gadget-user-complete';
import { LabComplete } from 'node_js/models/business/lab/lab-complete';
import { LabUserComplete } from 'node_js/models/business/lab/lab-user-complete';
import { UserSafe } from 'node_js/models/business/user-safe';
import { RoleEnum } from 'node_js/models/enums/role';
import { SnackBarColor } from 'node_js/models/enums/snackbar-color';
import { LabUser } from 'node_js/models/tables/lab_user';
import { UserContact } from 'node_js/models/tables/user_contact';
import { GadgetService } from 'src/app/services/gadget.service';
import { LabService } from 'src/app/services/lab.service';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { presentSnackBar, generateId, presentLoading } from 'src/global';
import { MatDialogRef } from '@angular/material/dialog';
import { Platform } from '@ionic/angular';
import { AndroidBackPriority } from 'node_js/models/enums/android-back-priority';
import { RoleHelpDialogComponent } from '../dialogs/role-help-dialog/role-help-dialog.component';
import { TutorialService } from 'src/app/services/tutorial.service';
import { LanguageService } from 'src/app/services/language.service';

@Component({
  selector: 'gadget-user-list',
  templateUrl: './gadget-user-list.component.html',
  styleUrls: ['./gadget-user-list.component.scss'],
})
export class GadgetUserListComponent implements OnInit, OnDestroy {


  @Input() labComplete: LabComplete;
  // Remove gadgetComplete input since we'll get it from the service

  isDialog = false;

  // Getter to get gadgetComplete from the service
  get gadgetComplete(): GadgetComplete {
    return this.labService.currentSelectedGadget;
  }

  RoleEnum = RoleEnum;

  clonedGadgetUsersComplete: GadgetUserComplete[] = []

  isThereAUserBeingDeleted: boolean = false;

  // Back button subscription
  private backButtonSub: any;

  constructor(public userAuthService: UserAuthService, private matSnackBar: MatSnackBar, private matDialog: MatDialog,
    protected gadgetService: GadgetService, protected labService: LabService, public tutorialService: TutorialService, @Optional() @Inject(MAT_DIALOG_DATA) private data: { labComplete: LabComplete, gadgetComplete: GadgetComplete },
    @Optional() private dialogRef: MatDialogRef<GadgetUserListComponent>, private platform: Platform, private languageService: LanguageService) {
    if (data) {
      this.labComplete = data.labComplete;
      // Note: gadgetComplete is now a getter from the service
      this.isDialog = true;
    }
  }

  ngOnInit() {
    console.log("gadget complete", this.gadgetComplete, this.labComplete)

    // Set up subscriptions
    this.setupSubscriptions();

    // Initialize immediately if data is available
    if (this.gadgetComplete && this.labComplete) {
      this.resetMembers();
    }

    // Set up back button handler if this component is loaded as a dialog
    if (this.isDialog) {
      this.handleBackButton();
    }
  }



  private setupSubscriptions() {
    this.gadgetService.onCurrentSelectedGadgetUsersCreated.subscribe((newGadgetUsersComplete: GadgetUserComplete[]) => {
      // Only proceed if gadgetComplete and labComplete are available
      if (!this.gadgetComplete) {
        console.warn('[GadgetUserList] gadgetComplete not available, skipping user update');
        return;
      }

      if (!this.labComplete) {
        console.warn('[GadgetUserList] labComplete not available, skipping user update');
        return;
      }

      for (let i = 0; i < newGadgetUsersComplete.length; i++) {
        let newGadgetUserComplete = newGadgetUsersComplete[i];
        if (newGadgetUserComplete.gadgetUser.gadgetId == this.gadgetComplete.gadget.id) {
          let clonedGadgetUserComplete = JSON.parse(JSON.stringify(newGadgetUserComplete));
          // Set newRoleId to roleId for editing (backend only sends roleId)
          if (!clonedGadgetUserComplete.gadgetUser.newRoleId) {
            clonedGadgetUserComplete.gadgetUser.newRoleId = clonedGadgetUserComplete.gadgetUser.roleId;
          }
          this.addLabUserCompleteToClonedGadgetUser(clonedGadgetUserComplete);
          this.clonedGadgetUsersComplete.push(clonedGadgetUserComplete);
        }
      }
      console.log("updated users", this.clonedGadgetUsersComplete)
    })

    this.gadgetService.currentSelectedGadgetUserSettingsChanged.subscribe(() => {
      // Only proceed if gadgetComplete is available
      if (this.gadgetComplete) {
        this.resetMembers();
      }
    })

    // Subscribe to lab user created events to update labUserComplete for any gadget users
    // This handles the case where a lab user is created after gadget users are already displayed
    this.labService.currentSelectedLabUserCreated.subscribe((labUserComplete: LabUserComplete) => {
      // Only proceed if this is for the current lab and we have a gadget selected
      if (this.labComplete && this.labComplete.lab.id === labUserComplete.labUser.labId && this.gadgetComplete) {
        // Update any gadget users that might be missing their labUserComplete
        for (let i = 0; i < this.clonedGadgetUsersComplete.length; i++) {
          let gadgetUserComplete = this.clonedGadgetUsersComplete[i];
          if (gadgetUserComplete.gadgetUser.userId === labUserComplete.labUser.userId && !gadgetUserComplete.labUserComplete) {
            console.log('[GadgetUserList] Found gadget user missing labUserComplete, adding it now');
            gadgetUserComplete.labUserComplete = JSON.parse(JSON.stringify(labUserComplete));
          }
        }
      }
    })
  }


  resetMembers() {
    // Safety check - only proceed if both inputs are available
    if (!this.gadgetComplete || !this.labComplete) {
      console.warn('[GadgetUserList] Cannot reset members - inputs not available:', {
        gadgetComplete: !!this.gadgetComplete,
        labComplete: !!this.labComplete
      });
      return;
    }

    // Safety check for gadgetUsersComplete array
    if (!this.gadgetComplete.gadgetUsersComplete || this.gadgetComplete.gadgetUsersComplete.length === 0) {
      console.warn('[GadgetUserList] No gadget users available for gadget:', this.gadgetComplete.gadget.id);
      this.clonedGadgetUsersComplete = [];
      return;
    }

    // IMPORTANT: Clone the gadget users to prevent modifying the original gadgetComplete object
    // This ensures that unsaved changes don't affect the original data
    console.log('[GadgetUserList] Cloning gadget users to prevent modifying original data');
    this.clonedGadgetUsersComplete = JSON.parse(JSON.stringify(this.gadgetComplete.gadgetUsersComplete));

    for (let i = 0; i < this.clonedGadgetUsersComplete.length; i++) {
      let clonedGadgetUserComplete = this.clonedGadgetUsersComplete[i];
      // Set the new role ID to the current role ID initially
      clonedGadgetUserComplete.gadgetUser.newRoleId = clonedGadgetUserComplete.gadgetUser.roleId;
      this.addLabUserCompleteToClonedGadgetUser(clonedGadgetUserComplete);
    }

    console.log('[GadgetUserList] Members reset successfully, count:', this.clonedGadgetUsersComplete.length);
    console.log('[GadgetUserList] Working with cloned data - original gadgetComplete object is safe from modifications');
  }

  addLabUserCompleteToClonedGadgetUser(clonedGadgetUserComplete: GadgetUserComplete) {
    // First try to find in the current labComplete
    if (this.labComplete && this.labComplete.labUsersComplete) {
      for (let i = 0; i < this.labComplete.labUsersComplete.length; i++) {
        let labUserComplete = this.labComplete.labUsersComplete[i];
        if (labUserComplete.labUser.userId == clonedGadgetUserComplete.gadgetUser.userId) {
          // Clone the lab user data to prevent modifying the original
          clonedGadgetUserComplete.labUserComplete = JSON.parse(JSON.stringify(labUserComplete));
          // Ensure the new role ID is set (this is safe since we're working with cloned data)
          clonedGadgetUserComplete.gadgetUser.newRoleId = clonedGadgetUserComplete.gadgetUser.roleId;
          return;
        }
      }
    }

    // If not found in current labComplete, search in the source data from userAuthService
    // This handles cases where the lab user was just created and might not be in labComplete yet
    if (this.userAuthService.currentUserComplete && this.userAuthService.currentUserComplete.labsComplete) {
      for (let i = 0; i < this.userAuthService.currentUserComplete.labsComplete.length; i++) {
        let labComplete = this.userAuthService.currentUserComplete.labsComplete[i];
        if (labComplete.lab.id === this.labComplete?.lab?.id && labComplete.labUsersComplete) {
          for (let j = 0; j < labComplete.labUsersComplete.length; j++) {
            let labUserComplete = labComplete.labUsersComplete[j];
            if (labUserComplete.labUser.userId == clonedGadgetUserComplete.gadgetUser.userId) {
              // Clone the lab user data to prevent modifying the original
              clonedGadgetUserComplete.labUserComplete = JSON.parse(JSON.stringify(labUserComplete));
              // Ensure the new role ID is set
              clonedGadgetUserComplete.gadgetUser.newRoleId = clonedGadgetUserComplete.gadgetUser.roleId;
              return;
            }
          }
        }
      }
    }

    // If still not found, log a warning but don't prevent the gadget user from being added
    // The lab user might be created shortly after, and the subscription will update it
    // Still ensure newRoleId is set for the dropdown to work
    if (!clonedGadgetUserComplete.gadgetUser.newRoleId) {
      clonedGadgetUserComplete.gadgetUser.newRoleId = clonedGadgetUserComplete.gadgetUser.roleId;
    }
    console.warn('[GadgetUserList] Could not find labUserComplete for gadget user:', clonedGadgetUserComplete.gadgetUser.userId, '- will be set when lab user is available');
  }

  onRoleChange(changedUserComplete: GadgetUserComplete) {
    // Check if we are demoting an owner (either self or by admin)
    // If the user was an owner, and is now being set to NOT owner...
    if (changedUserComplete.gadgetUser.roleId == RoleEnum.OWNER && changedUserComplete.gadgetUser.newRoleId != RoleEnum.OWNER) {

      // Check if there is anyone else who is currently set to be an OWNER
      // We check newRoleId because that reflects the current state of the UI (including other users we might have just promoted)
      const hasOtherOwner = this.clonedGadgetUsersComplete.some(u =>
        u.gadgetUser.userId !== changedUserComplete.gadgetUser.userId &&
        u.gadgetUser.newRoleId == RoleEnum.OWNER
      );

      if (!hasOtherOwner) {
        presentSnackBar("Gadget must have at least one owner. Promote someone else first.", SnackBarColor.DANGER, this.matSnackBar);

        // Revert selection immediately
        setTimeout(() => {
          changedUserComplete.gadgetUser.newRoleId = RoleEnum.OWNER;
        });
        return;
      }
    }

    if (changedUserComplete.gadgetUser.newRoleId === RoleEnum.OWNER) {
      // User selected OWNER. Find the current owner(s) and demote them to ADMIN
      for (const user of this.clonedGadgetUsersComplete) {
        // Skip the user who just got promoted
        if (user.gadgetUser.userId === changedUserComplete.gadgetUser.userId) {
          continue;
        }

        // If this user was effectively the owner (roleId or newRoleId is OWNER), demote them
        const effectiveRole = user.gadgetUser.newRoleId || user.gadgetUser.roleId;
        if (effectiveRole === RoleEnum.OWNER) {
          user.gadgetUser.newRoleId = RoleEnum.ADMIN;
          // Trigger any visual updates if necessary (ngModel should handle it)
          console.log(`[GadgetUserList] Demoted previous owner ${user.userSafe?.nickname} to ADMIN`);
        }
      }
    }
  }





  canPromoteToOwner(targetUserComplete: GadgetUserComplete): boolean {
    // If the target is ALREADY the owner, the option is valid (no-op)
    if (targetUserComplete.gadgetUser.roleId == RoleEnum.OWNER) {
      return true;
    }

    // Identify the current owner(s) that would be displaced
    // We check against the original roleId (DB state) to determine who effectively owns it now
    const currentOwners = this.clonedGadgetUsersComplete.filter(u => u.gadgetUser.roleId == RoleEnum.OWNER);

    // If there is no current owner (unsynced state?), default to true
    if (currentOwners.length === 0) {
      return true;
    }

    // Check if the current user (ME) has permission to edit the current owner(s)
    // We need our own LabUser and GadgetUser records
    if (!this.labComplete?.currentLabUserComplete?.labUser || !this.gadgetComplete?.currentGadgetUserComplete?.gadgetUser) {
      return false; // Safely disable if my data isn't missing
    }

    const myLabUser = this.labComplete.currentLabUserComplete.labUser;
    const myGadgetUser = this.gadgetComplete.currentGadgetUserComplete.gadgetUser;

    for (const owner of currentOwners) {
      // Find the owner's lab user record
      if (!owner.labUserComplete?.labUser) {
        // Edge case: Owner not found in lab?
        continue;
      }

      const ownerLabUser = owner.labUserComplete.labUser;
      const ownerGadgetUser = owner.gadgetUser;

      // Can I edit this owner?
      // false, false -> not checking super admin special cases, verifying against exact permission logic
      if (!this.gadgetService.canUserEditOtherGadgetUser(myLabUser, ownerLabUser, myGadgetUser, ownerGadgetUser, false, false)) {
        return false; // Blocked: I can't demote this owner
      }
    }

    return true;
  }

  getRoleName(roleId: RoleEnum): string {
    switch (roleId) {
      case RoleEnum.OWNER: return 'Owner';
      case RoleEnum.ADMIN: return 'Admin';
      case RoleEnum.CREATOR: return 'Creator';
      case RoleEnum.SUPPORT: return 'Support';
      case RoleEnum.USER: return 'Viewer';
      default: return 'Unknown';
    }
  }

  getGadgetRoleTooltip(roleId: RoleEnum, isInherited: boolean = false): string {
    let tooltip = 'Can view and use this Gadget.';

    switch (roleId) {
      case RoleEnum.OWNER:
        tooltip = isInherited
          ? 'Inherited from the Lab Owner role. Has full control over this Gadget.'
          : 'Has full control over this Gadget.';
        break;
      case RoleEnum.ADMIN:
        tooltip = isInherited
          ? 'Inherited from the Lab Admin role. Can manage this Gadget and its members.'
          : 'Can manage this Gadget and its members.';
        break;
      case RoleEnum.CREATOR:
        tooltip = 'Can edit this Gadget, its components, and controller setup.';
        break;
      case RoleEnum.SUPPORT:
        tooltip = 'Can help monitor and troubleshoot this Gadget.';
        break;
      case RoleEnum.USER:
      default:
        tooltip = 'Can view and use this Gadget.';
        break;
    }

    return this.languageService.translateUiLiteral(tooltip);
  }

  updateGadgetUserSettings() {
    // Safety check - only proceed if both inputs are available
    if (!this.gadgetComplete || !this.labComplete) {
      console.error('[GadgetUserList] Cannot update gadget user settings - inputs not available');
      presentSnackBar("Cannot update settings - data not available", SnackBarColor.DANGER, this.matSnackBar);
      return;
    }

    let loadingRef = presentLoading(this.matDialog);

    // DEBUG: Log payload to see what we are sending
    console.log('[updateGadgetUserSettings] Sending payload:', JSON.parse(JSON.stringify(this.clonedGadgetUsersComplete)));
    this.clonedGadgetUsersComplete.forEach(u => {
      console.log(`User: ${u.gadgetUser.userId}, Role: ${u.gadgetUser.roleId}, NEW Role: ${u.gadgetUser.newRoleId}`);
    });

    this.gadgetService.updateGadgetUserSettings(this.userAuthService, this.gadgetComplete.gadget.id, this.clonedGadgetUsersComplete).then((gadgetUsersComplete: GadgetUserComplete[]) => {

      this.gadgetService.upsertGadgetUsersCompleteLocally(gadgetUsersComplete, this.userAuthService);
      presentSnackBar("Gadget members have been updated", SnackBarColor.SUCCESS, this.matSnackBar);
    }).catch(() => {
      presentSnackBar(this.languageService.instant('generic.unexpected-error'), SnackBarColor.DANGER, this.matSnackBar);
    }).finally(() => {

      loadingRef.close();
    })
  }

  /**
   * Handle back button press when component is loaded as a dialog
   */
  handleBackButton() {
    console.log('[GadgetUserList] handleBackButton called, dialogRef exists:', !!this.dialogRef);

    // Only set up back button handler if this component is loaded as a dialog
    if (this.dialogRef) {
      console.log('[GadgetUserList] Setting up back button handler with priority:', AndroidBackPriority.DIALOG);
      this.backButtonSub = this.platform.backButton.subscribeWithPriority(AndroidBackPriority.DIALOG, () => {
        console.log('[GadgetUserList] 🎯 BACK BUTTON PRESSED! Closing dialog...');
        this.dialogRef.close();
      });
      console.log('[GadgetUserList] ✅ Back button handler set up successfully');
    } else {
      console.log('[GadgetUserList] Not loaded as dialog, skipping back button handler');
    }
  }

  /**
   * Clean up back button subscription when component is destroyed
   */
  ngOnDestroy() {
    console.log('[GadgetUserList] ngOnDestroy called, cleaning up back button subscription');
    this.cleanupBackButtonSubscription();
  }

  /**
   * Clean up back button subscription manually
   */
  private cleanupBackButtonSubscription() {
    if (this.backButtonSub) {
      try {
        this.backButtonSub.unsubscribe();
        console.log('[GadgetUserList] ✅ Back button subscription cleaned up');
      } catch (err) {
        console.warn('[GadgetUserList] Error cleaning up back button subscription:', err);
      }
      this.backButtonSub = null;
    }
  }

  /**
   * Public method to cleanup back button subscription before dialog closes
   */
  public cleanupBeforeClose() {
    console.log('[GadgetUserList] cleanupBeforeClose called');
    this.cleanupBackButtonSubscription();
  }

  startGadgetUsersTutorial() {
    this.tutorialService.startGadgetUsersTutorial();
  }

  openRoleHelp() {
    if (this.tutorialService.isStepActive('gadget-users-help')) {
      this.tutorialService.noteRoleHelpOpened();
    }

    const dialogRef = this.matDialog.open(RoleHelpDialogComponent, {
      width: '400px',
      autoFocus: false,
      panelClass: 'role-help-dialog-panel'
    });

    dialogRef.afterClosed().subscribe(() => {
      this.tutorialService.noteRoleHelpClosed();

      if (this.tutorialService.justCompletedTutorialId === 'gadget-users') {
        this.dialogRef?.close();
      }
    });
  }
}
